import {useNavigate} from 'react-router-dom';
import {IAppliedRule} from '../../incase-domain/appliedRules';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import {PlayerLabel} from '../../incase-domain/playerLabel';
import {IPresentation} from '../../incase-domain/presentation';
import {ProtectionInfo} from '../../incase-domain/presentationProtection';
import {IPresentationShare} from '../../incase-domain/presentationShare';
import {ISlide} from '../../incase-domain/slide';
import {TEMPLATE} from '../../service/template/types';
import {
    IDeleteEmittedSharedPresentationAction,
    IExportPresentationAction,
    IExportPresentationEndAction,
    IFetchPresentationProtectionAction,
    IResetPresentationReducer,
    ISelectPresentationToEditAction,
    ISetDeletedEmittedSharedPresentation,
    ISetEmittedSharedPresentation,
    ISetPresentationSettingsModalAction,
    ISetPresentationValidationErrorsAction,
    ISetSelectedViewAction,
    IUpdatePlayerLabelsAction,
    IUpdatePresentationProtection,
    IUpdatePresentationWithEmittedPresentationAction,
    IUpdateSelectedPresentationAction,
    IUpdateSelectedPresentationAppliedRulesAction,
    IUpdateSelectedPresentationSlideIndexListAction,
    IUpdateSelectedPresentationSlidesAction,
    IUpdateSettingsConfigAction,
    IUpdateSharedPresentationWithEmittedSharedPresentationAction,
} from './types/presentation.types';

export const SELECT_PRESENTATION_TO_EDIT = 'SELECT_PRESENTATION_TO_EDIT';
export const EXPORT_PRESENTATION = 'EXPORT_PRESENTATION';
export const EXPORT_PRESENTATION_END = 'EXPORT_PRESENTATION_END';
export const UPDATE_SELECTED_PRESENTATION = 'UPDATE_SELECTED_PRESENTATION';
export const UPDATE_SELECTED_PRESENTATION_SLIDES = 'UPDATE_SELECTED_PRESENTATION_SLIDES';
export const UPDATE_SELECTED_PRESENTATION_SLIDE_INDEX_LIST = 'UPDATE_SELECTED_PRESENTATION_SLIDE_INDEX_LIST';
export const UPDATE_SELECTED_PRESENTATION_APPLIED_RULES = 'UPDATE_SELECTED_PRESENTATION_APPLIED_RULES';
export const UPDATE_SETTINGS_CONFIG = 'UPDATE_SETTINGS_CONFIG';
export const FETCH_PRESENTATION_PROTECTION = 'FETCH_PRESENTATION_PROTECTION';
export const UPDATE_PRESENTATION_PROTECTION = 'UPDATE_PRESENTATION_PROTECTION';
export const UPDATE_PLAYER_LABELS = 'UPDATE_PLAYER_LABELS';
export const SET_PRESENTATION_VALIDATION_ERRORS = 'SET_PRESENTATION_VALIDATION_ERRORS';
export const FETCH_OWNER_SHARED_PRESENTATIONS = 'FETCH_OWNER_SHARED_PRESENTATIONS';
export const UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION = 'UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION';
export const SET_EMITTED_SHARED_PRESENTATION = 'SET_EMITTED_SHARED_PRESENTATION';
export const SET_DELETED_EMITTED_SHARED_PRESENTATION = 'SET_DELETED_EMITTED_SHARED_PRESENTATION';
export const DELETE_EMITTED_SHARED_PRESENTATION_ACTION = 'DELETE_EMITTED_SHARED_PRESENTATION_ACTION';
export const UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION =
    'UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION';
export const RESET_PRESENTATION_REDUCER = 'RESET_PRESENTATION_REDUCER';
export const SET_SELECTED_VIEW = 'SET_SELECTED_VIEW';
export const SET_PRESENTATION_SETTINGS_MODAL = 'SET_PRESENTATION_SETTINGS_MODAL';

export const selectPresentationToEditAction = (
    presentationId: string,
    navigate: ReturnType<typeof useNavigate>,
    template?: TEMPLATE
): ISelectPresentationToEditAction => ({
    type: SELECT_PRESENTATION_TO_EDIT,
    payload: {
        presentationId,
        navigate,
        template,
    },
});

export const exportPresentationAction = (presentationId: string): IExportPresentationAction => ({
    type: EXPORT_PRESENTATION,
    presentationId,
});

export const exportPresentationEndAction = (presentationId: string): IExportPresentationEndAction => ({
    type: EXPORT_PRESENTATION_END,
    presentationId,
});

export const updateSelectedPresentationAction = (presentation: IPresentation): IUpdateSelectedPresentationAction => ({
    type: UPDATE_SELECTED_PRESENTATION,
    presentation,
});

export const updateSelectedPresentationSlidesAction = (slides: ISlide[]): IUpdateSelectedPresentationSlidesAction => ({
    type: UPDATE_SELECTED_PRESENTATION_SLIDES,
    slides,
});

export const updateSelectedPresentationSlideIndexListAction = (
    slideIndexList: string[]
): IUpdateSelectedPresentationSlideIndexListAction => ({
    type: UPDATE_SELECTED_PRESENTATION_SLIDE_INDEX_LIST,
    slideIndexList,
});

export const updateSelectedPresentationAppliedRulesAction = (
    appliedRules: IAppliedRule[]
): IUpdateSelectedPresentationAppliedRulesAction => ({
    type: UPDATE_SELECTED_PRESENTATION_APPLIED_RULES,
    appliedRules,
});

export const updateSettingsConfigAction = (protection: ProtectionInfo): IUpdateSettingsConfigAction => ({
    type: UPDATE_SETTINGS_CONFIG,
    protection,
});

export const fetchPresentationProtectionAction = (presentationId: string): IFetchPresentationProtectionAction => ({
    type: FETCH_PRESENTATION_PROTECTION,
    presentationId,
});

export const updatePresentationProtectionAction = (
    presentationId: string,
    protection: ProtectionInfo,
    silent?: boolean
): IUpdatePresentationProtection => ({
    type: UPDATE_PRESENTATION_PROTECTION,
    presentationId,
    protection,
    silent,
});

export const updatePlayerLabelsAction = (labels: PlayerLabel[], presentationId: string): IUpdatePlayerLabelsAction => ({
    type: UPDATE_PLAYER_LABELS,
    labels,
    presentationId,
});

export const setPresentationValidationErrorsAction = (errors: Record<string, string[]>): ISetPresentationValidationErrorsAction => ({
    type: SET_PRESENTATION_VALIDATION_ERRORS,
    errors,
});

export const updatePresentationWithEmittedPresentationAction = ({
    presentation,
    labels,
    protection,
}: {
    presentation?: IPresentation;
    labels?: PlayerLabel[];
    protection?: ProtectionInfo;
}): IUpdatePresentationWithEmittedPresentationAction => ({
    type: UPDATE_PRESENTATION_WITH_EMITTED_PRESENTATION_ACTION,
    presentation,
    labels,
    protection,
});

export const updateSharedPresentationWithEmittedSharedPresentationAction = (
    sharedPresentation: IPresentationShare
): IUpdateSharedPresentationWithEmittedSharedPresentationAction => ({
    type: UPDATE_SHARED_PRESENTATION_WITH_EMITTED_SHARED_PRESENTATION_ACTION,
    sharedPresentation,
});

export const setEmittedSharedPresentationAction = (sharedPresentation: IPresentationShare): ISetEmittedSharedPresentation => ({
    type: SET_EMITTED_SHARED_PRESENTATION,
    sharedPresentation,
});

export const setDeletedEmittedSharedPresentationAction = (presentationIds: string[]): ISetDeletedEmittedSharedPresentation => ({
    type: SET_DELETED_EMITTED_SHARED_PRESENTATION,
    presentationIds,
});

export const deleteEmittedSharedPresentationAction = (presentationIds: string[]): IDeleteEmittedSharedPresentationAction => ({
    type: DELETE_EMITTED_SHARED_PRESENTATION_ACTION,
    presentationIds,
});

export const resetPresentationReducerAction = (): IResetPresentationReducer => ({
    type: RESET_PRESENTATION_REDUCER,
});

export const setSelectedView = (selectedView: DeviceType): ISetSelectedViewAction => ({
    type: SET_SELECTED_VIEW,
    selectedView,
});

export const setPresentationSettingsModalAction = (open: boolean, tab?: number): ISetPresentationSettingsModalAction => ({
    type: SET_PRESENTATION_SETTINGS_MODAL,
    payload: {
        open,
        tab,
    },
});
